/**
 * Điều khoản của một trận: chấp ván, chấp bi, và trận có được hoà hay không.
 *
 * Gom vào một chỗ vì ba màn đang in cùng mấy dòng này — màn trước trận, màn xác nhận
 * tỉ số, và dải thông báo — và ba bản chép tay là ba chỗ để lệch nhau. Chỉ gom phần
 * TÍNH; phần hiển thị vẫn ở từng màn, nên không màn nào đổi bố cục vì màn khác.
 *
 * Số ở đây đều do Broduct Core báo và backend chép lại. Không tính gì.
 */

export interface DieuKhoanTranInput {
  /** Dương = người thách chấp ván cho người nhận. Quy ước có từ trước. */
  handicappingPoint?: number | null;
  /** Dương = ghế đó ĐƯỢC chấp bi. Vắng ở trận cũ. */
  challengerBallSpots?: number | null;
  acceptorBallSpots?: number | null;
  /** Vắng ở trận quyết trước khi có ma trận. */
  drawRule?: string | null;
  race?: number | null;
  challengerRaceTarget?: number | null;
  acceptorRaceTarget?: number | null;
}

export interface BenChap {
  /** 'challenger' | 'acceptor' — bên ĐI chấp. */
  nguoiChap: "challenger" | "acceptor";
  nguoiDuocChap: "challenger" | "acceptor";
  soLuong: number;
}

/** Ai chấp ai mấy ván. Null khi không có chấp ván. */
export const chapVan = (tran: DieuKhoanTranInput): BenChap | null => {
  const diem = tran.handicappingPoint ?? 0;
  if (!diem) return null;
  return diem > 0
    ? { nguoiChap: "challenger", nguoiDuocChap: "acceptor", soLuong: Math.abs(diem) }
    : { nguoiChap: "acceptor", nguoiDuocChap: "challenger", soLuong: Math.abs(diem) };
};

/**
 * Ai chấp ai mấy bi. Null khi không có chấp bi — và đó là trường hợp thường gặp:
 * ma trận v1.0 để chấp bi bằng 0 ở mọi ô, còn trận cũ thì không có trường này.
 *
 * Đọc DẤU của ghế người thách, đúng quy ước đã dùng cho `handicappingPoint`: dương
 * nghĩa là ghế đó được chấp, nên người chấp là ghế kia.
 */
export const chapBi = (tran: DieuKhoanTranInput): BenChap | null => {
  const cuaNguoiThach = tran.challengerBallSpots ?? 0;
  if (!cuaNguoiThach) return null;
  return cuaNguoiThach > 0
    ? { nguoiChap: "acceptor", nguoiDuocChap: "challenger", soLuong: Math.abs(cuaNguoiThach) }
    : { nguoiChap: "challenger", nguoiDuocChap: "acceptor", soLuong: Math.abs(cuaNguoiThach) };
};

/**
 * Trận này có được hoà không, và hoà ở tỉ số nào.
 *
 * Đọc `drawRule` của TRẬN chứ không suy từ loại giải, để dòng hiển thị và luật thật
 * sự được áp cùng đọc một nguồn. Trận cũ không có `drawRule` thì giữ nguyên cách cũ:
 * suy từ loại giải, đúng từng chữ như trước.
 */
export const hoaDuoc = (tran: DieuKhoanTranInput, loaiGiai?: string | null): boolean =>
  tran.drawRule ? tran.drawRule !== "NO_DRAW" : loaiGiai !== "ELIMINATION";

/**
 * Tỉ số hoà: cả hai cùng kém CHẠM CHUNG một ván.
 *
 * Kèo là CHẤP TRƯỚC - bên yếu vào bàn với số ván được chấp đã nằm sẵn trên bảng
 * điểm - nên tỉ số cuối vẫn đo bằng một chạm chung cho cả hai, đúng như backend,
 * trọng tài và màn quản trị chấm. Hai chạm riêng của kèo chỉ để tính số ván chấp.
 * Thiếu chạm chung thì lấy chạm dài hơn của kèo, vì lưới chỉ rút ngắn bên yếu.
 */
export const tiSoHoa = (tran: DieuKhoanTranInput): [number, number] | null => {
  const cham = tran.race || Math.max(tran.challengerRaceTarget ?? 0, tran.acceptorRaceTarget ?? 0);
  if (cham < 1) return null;
  return [cham - 1, cham - 1];
};

/**
 * Trận này ĐÃ CÓ điều khoản chưa.
 *
 * Khác hẳn "đánh đồng". Đánh đồng là một câu trả lời - hệ thống chấp đã xem cặp này
 * và nói hai bên ngang nhau. Chưa có điều khoản là CHƯA CÓ CÂU TRẢ LỜI NÀO: trận vừa
 * bốc cặp, chưa ai báo giá. In "Đánh đồng" cho trường hợp sau là bịa ra một kết luận
 * chưa ai đưa ra, và nó sai đúng vào những cặp lệch trình nhất.
 *
 * Đọc chạm từng ghế chứ không đọc chênh ván: một kèo đánh đồng thật cũng có chênh
 * bằng 0, nên chênh không phân biệt được hai trường hợp. Chạm từng ghế thì chỉ có
 * khi đã báo giá.
 */
export const coDieuKhoan = (tran: DieuKhoanTranInput): boolean =>
  tran.challengerRaceTarget != null && tran.acceptorRaceTarget != null;

export interface KeoChapView {
  van: number;
  bi: number;
  tenNguoiChap: string;
  tenNguoiDuocChap: string;
}

/**
 * Kèo của một trận, gộp cả ván lẫn bi, cho thẻ cặp đấu in một dòng.
 *
 * Nhận tên hai ghế từ chỗ gọi chứ không tự tra: component dùng chung chỉ có danh
 * sách người chơi theo thứ tự hiển thị, mà thứ tự đó KHÔNG phải thứ tự thách/nhận -
 * trận đã có kết quả được xếp người thắng lên trên.
 *
 * Null khi đánh đồng hoặc khi trận chưa có điều khoản nào (vừa bốc cặp, chưa ai nhận
 * kèo). Hai trường hợp đó thẻ không in dòng kèo, vì chưa có gì để in.
 */
export const keoChap = (
  tran: DieuKhoanTranInput,
  tenNguoiThach: string,
  tenNguoiNhan: string
): KeoChapView | null => {
  if (!coDieuKhoan(tran)) return null;

  const van = chapVan(tran);
  const bi = chapBi(tran);

  // Hai phần luôn cùng chiều - ma trận chấp cho MỘT bên yếu hơn, không bao giờ chia
  // mỗi bên một thứ - nên bên nào có mặt thì bên đó định chiều.
  const ben = van ?? bi;
  const ten = (ghe: "challenger" | "acceptor") => (ghe === "challenger" ? tenNguoiThach : tenNguoiNhan);

  return {
    van: van?.soLuong ?? 0,
    bi: bi?.soLuong ?? 0,
    tenNguoiChap: ben ? ten(ben.nguoiChap) : "",
    tenNguoiDuocChap: ben ? ten(ben.nguoiDuocChap) : "",
  };
};

